import React, { useRef } from 'react';
import { toast } from 'sonner';
import type { WebsiteResponse, UpdateWebsiteRequest } from '@troia-v3/types';
import { buildExportPayload, parseImport, slugifyFilename, downloadJson } from '../utils/websiteTransfer';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

/**
 * Lógica de exportar/importar configuração de um website, compartilhada entre
 * o card (view Cards) e a linha de tabela (view Lista). Mantém o input de
 * arquivo escondido + confirmação antes de sobrescrever o site.
 */
export function useWebsiteImportExport(
  website: WebsiteResponse,
  onImport: (id: string, data: UpdateWebsiteRequest) => void,
) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();

  const handleExport = () => {
    downloadJson(slugifyFilename(website.name), buildExportPayload(website));
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reimportar o mesmo arquivo

    if (!file) return;

    let text: string;
    try {
      text = await file.text();
    } catch {
      toast.error('Não foi possível ler o arquivo.');
      return;
    }

    const result = parseImport(text, website);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }

    const confirmed = await confirm({
      message: 'Isso vai substituir o conteúdo e as configurações deste site. Continuar?',
      confirmButtonText: 'Substituir',
      variant: 'default',
    });
    if (!confirmed) return;

    onImport(website.id, result.data);
  };

  return { fileInputRef, handleExport, handleImportClick, handleFileChange };
}
